// siteInvoiceService.ts
// Job 55 — historic / reference invoices attached to a site that deliberately
// have no bill run. Backed by bv_site_invoice (migration 073) and the existing
// bv-attachments storage bucket, under a site-scoped path. These files are never
// parsed and never contribute to any comparison or total.

import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import type { ApiResponse } from '../types/billValidationTypes';
import type { BvSiteInvoice } from '../types/billTypes';
import { validateInvoicePdf } from '../utils/invoiceDrop';

const BUCKET = 'bv-attachments';

async function getOrgId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase
    .from('base_organisation_members')
    .select('organisation_id')
    .eq('user_id', user.id)
    .limit(1)
    .single();
  return data?.organisation_id ?? null;
}

async function getUserId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id ?? null;
}

// Site-scoped path, mirroring the manual bill upload but keyed on the site since
// these invoices have no bill run to hang off.
function storagePath(orgId: string, siteId: string, fileName: string): string {
  const uid = crypto.randomUUID();
  const safe = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
  return `${orgId}/sites/${siteId}/reference-invoices/${uid}_${safe}`;
}

export const siteInvoiceService = {
  list: async (siteId: string): Promise<ApiResponse<BvSiteInvoice[]>> => {
    try {
      const { data, error } = await supabase
        .from('bv_site_invoice')
        .select('*')
        .eq('site_id', siteId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return { success: true, data: (data ?? []) as BvSiteInvoice[] };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  upload: async (siteId: string, file: File, note?: string): Promise<ApiResponse<BvSiteInvoice>> => {
    try {
      const invalid = validateInvoicePdf(file);
      if (invalid) return { success: false, error: invalid };

      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };

      const path = storagePath(orgId, siteId, file.name);

      const { error: uploadErr } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, { upsert: false });
      if (uploadErr) throw uploadErr;

      const { data, error: dbErr } = await supabase
        .from('bv_site_invoice')
        .insert({
          org_id: orgId,
          site_id: siteId,
          storage_path: path,
          filename: file.name,
          note: note?.trim() || null,
          uploaded_by: await getUserId(),
        })
        .select()
        .single();
      if (dbErr) {
        // Row failed — don't leave an orphaned file behind.
        await supabase.storage.from(BUCKET).remove([path]);
        throw dbErr;
      }

      return { success: true, data: data as BvSiteInvoice };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  // Row first, then the storage object — matching billService.removeInvoice's
  // ordering so an orphaned file is preferred over a row pointing at a deleted PDF.
  remove: async (id: string): Promise<ApiResponse<null>> => {
    try {
      const { data: before } = await supabase
        .from('bv_site_invoice')
        .select('storage_path')
        .eq('id', id)
        .single();

      const { error } = await supabase.from('bv_site_invoice').delete().eq('id', id);
      if (error) throw error;

      const path = (before as { storage_path?: string } | null)?.storage_path;
      if (path) {
        await supabase.storage.from(BUCKET).remove([path]);
      }

      return { success: true, data: null };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  getSignedUrl: async (storagePath: string, expiresIn = 3600): Promise<string | null> => {
    const { data } = await supabase.storage.from(BUCKET).createSignedUrl(storagePath, expiresIn);
    return data?.signedUrl ?? null;
  },
};
